/**
 * Plans v2 local caps, the part that opens nothing: the refusal SENTENCE.
 *
 * The in-app AI and the MCP server create items on the user's behalf. A full
 * plan must refuse them with a clear message, but must not pop a modal over
 * whatever the user is doing, so they use this module, which reads the
 * entitlements store and nothing else (no UI store, no invoke). The UI's gate,
 * which also opens the upgrade modal, is plans/localGate.js.
 */
import i18n from '../i18n';
import { checkLocalLimit, useEntitlementsStore } from '../store/entitlementsStore';
import { isReadOnlyCloudWorkspace } from './cloudAccess';

/** The refusal sentence for `checkLocalLimit`'s `{ kind, limit, used }`. */
export function localLimitMessage({ kind, limit, used }) {
  // Free's workspace cap is 0 extra workspaces: "0 / 0" would read as a bug.
  if (kind === 'workspaces' && limit === 0) return i18n.t('plans.localLimit.reasonWorkspaces');
  return i18n.t('plans.localLimit.reason', {
    used,
    limit,
    what: i18n.t(`plans.localLimit.kind.${kind}`, { count: limit }),
  });
}

/** True when the current plan caps `kind` at all (paid plans cap nothing locally). */
export const isCapped = (kind) => {
  const limit = useEntitlementsStore.getState().limits?.[kind];
  return limit !== null && limit !== undefined;
};

/** The refusal sentence when `adding` more `kind` do not fit next to `used`, or null. */
export function capRefusal(kind, used, adding = 1) {
  const outcome = checkLocalLimit(kind, used, adding);
  return outcome.ok ? null : localLimitMessage(outcome);
}

/**
 * The refusal sentence when `workspace` is an API workspace of the account's
 * own read-only cloud (plans/cloudAccess.isReadOnlyCloudWorkspace), or null.
 */
export function readOnlyWorkspaceRefusal(workspace) {
  return isReadOnlyCloudWorkspace(workspace) ? i18n.t('cloudLocal.readOnlyWorkspace') : null;
}

/**
 * Same, but counts only when the plan caps `kind`: `countFn` is async and may
 * read the store, which a paid plan never needs.
 */
export async function capRefusalFor(kind, countFn, adding = 1) {
  if (!isCapped(kind)) return null;
  return capRefusal(kind, await countFn(), adding);
}
